import React from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { Trade } from '../ActiveTradesPage';
import MagicButton from 'components/MagicButton/MagicButton';
import { AssetUrls } from 'config/assets';
import { useGetProfileQuery } from 'store/services/auth/authApi';
import { SetIcon } from 'components/SetIcon';
import { formatLanguage } from 'constants/languages';
import { copyToClipboard } from 'utils/copyToClipboard';
import { showSuccess, showError } from 'utils/toastNotifications';
import './PackingSlipModal.css';

const formatCondition = (condition: string): string => {
  const conditionMap: { [key: string]: string } = {
    'NEAR_MINT': 'NM',
    'NM': 'NM',
    'SLIGHTLY_PLAYED': 'LP',
    'LIGHTLY_PLAYED': 'LP',
    'LP': 'LP',
    'SP': 'LP',
    'MODERATELY_PLAYED': 'MP',
    'MP': 'MP',
    'HEAVILY_PLAYED': 'HP',
    'HP': 'HP',
    'DAMAGED': 'DMG',
    'DMG': 'DMG'
  };
  return conditionMap[condition] || condition;
};


const formatCountry = (country: string): string => {
  const code = country.toUpperCase().trim();
  try {
    const name = new Intl.DisplayNames(['en'], { type: 'region' }).of(code);
    return name ? name.toUpperCase() : code;
  } catch {
    return code;
  }
};

interface Props {
  trade: Trade;
  isOpen: boolean;
  onClose: () => void;
}

export default function PackingSlipModal({ trade, isOpen, onClose }: Props) {
  // Get sender info for the packing slip
  const user = useSelector((state: RootState) => state.auth.user);
  const { data: profile } = useGetProfileQuery();
  // Declared above the `!isOpen` early return so hook order stays stable.
  const [copied, setCopied] = React.useState(false);

  // Reset the confirmation each time the modal is reopened.
  React.useEffect(() => {
    if (!isOpen) setCopied(false);
  }, [isOpen]);

  // Extract sender's country from profile
  const senderCountry = profile?.shippingAddress?.country || profile?.shippingCountry || 'USA';

  if (!isOpen) return null;

  const copyAddressToClipboard = async () => {
    let addressLines = [
      trade.shippingAddress.name,
      trade.shippingAddress.street
    ];
    
    // Add street2 if it exists
    if (trade.shippingAddress.street2) {
      addressLines.push(trade.shippingAddress.street2);
    }
    
    addressLines.push(
      `${trade.shippingAddress.city}, ${trade.shippingAddress.state} ${trade.shippingAddress.zipCode}`,
      formatCountry(trade.shippingAddress.country)
    );
    
    const address = addressLines.join('\n');

    // The copy often succeeds only via the execCommand fallback, so the
    // confirmation must be driven by the result -- not by reaching the end of
    // a try block. See utils/copyToClipboard.
    if (await copyToClipboard(address)) {
      setCopied(true);
      showSuccess('Address copied to clipboard');
    } else {
      showError('Could not copy the address. Please select and copy it manually.');
    }
  };

  const printPackingSlip = () => {
    // Hide all other content during print
    const body = document.body;
    const allElements = body.querySelectorAll('*:not(.modal-overlay):not(.packing-slip-modal):not(.packing-slip-modal *)');
    
    // Add print-hide class to all non-modal elements
    allElements.forEach(element => {
      if (!element.closest('.modal-overlay')) {
        element.classList.add('print-hide');
      }
    });
    
    // Trigger print
    window.print();
    
    // Clean up - remove print-hide class after printing
    setTimeout(() => {
      allElements.forEach(element => {
        element.classList.remove('print-hide');
      });
    }, 1000);
  };

  const formatDate = () => {
    return new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const modalContent = (
    <div className="modal-overlay" onClick={onClose}>
      <div className="packing-slip-modal" onClick={(e) => e.stopPropagation()}>
        {/* Tearaway section - Shipping label address (print only) */}
        <div className="print-address-block">
          <div className="address-line">{trade.shippingAddress.name}</div>
          <div className="address-line">{trade.shippingAddress.street}</div>
          {trade.shippingAddress.street2 && (
            <div className="address-line">{trade.shippingAddress.street2}</div>
          )}
          <div className="address-line">
            {trade.shippingAddress.city}, {trade.shippingAddress.state} {trade.shippingAddress.zipCode}
          </div>
          <div className="address-line">{formatCountry(trade.shippingAddress.country)}</div>
        </div>

        {/* Dashed tearaway line (print only) */}
        <div className="tearaway-line"></div>

        <div className="modal-header">
          <h2>Packing Slip - Trade #{trade.tradeNumber} from {user?.username}</h2>
          <button className="close-button" onClick={onClose}>
            <img
              src={AssetUrls.icons.dropdowns.close}
              alt="Close"
              style={{ width: 16, height: 16 }}
            />
          </button>
        </div>

        <div className="packing-slip-content">
          <div className="slip-section ship-to-row">
            <div className="ship-to-column">
              <h3>Ship To:</h3>
              <div className="shipping-address">
                <div className="address-line">{trade.shippingAddress.name}</div>
                <div className="address-line">{trade.shippingAddress.street}</div>
                {trade.shippingAddress.street2 && (
                  <div className="address-line">{trade.shippingAddress.street2}</div>
                )}
                <div className="address-line">
                  {trade.shippingAddress.city}, {trade.shippingAddress.state} {trade.shippingAddress.zipCode}
                </div>
                <div className="address-line">{formatCountry(trade.shippingAddress.country)}</div>
              </div>
            </div>

            {/* What's Next? instruction box */}
            <div className="whats-next-box">
              <h4>What's Next?</h4>
              <ol>
                <li><strong>Check the cards</strong> - confirm correct condition, edition, and language.</li>
                <li><strong>Look good?</strong> Head to <span className="link-text">/receiving</span> and click "Complete Trade"</li>
                <li><strong>Something unexpected?</strong> Send your trading partner a message.</li>
                <li><strong>Need Help?</strong> Click "Summon Admin" for assistance from the team.</li>
              </ol>
            </div>
          </div>

          <div className="address-actions">
            <MagicButton
              icon={
                <img
                  src={`${AssetUrls.base}/assets/icons/SendCards/icon-print.svg`}
                  alt="Print"
                  style={{ width: 16, height: 16, filter: 'brightness(0) invert(1)' }}
                />
              }
              label="Print Packing Slip"
              color="purple"
              onPress={printPackingSlip}
            />
            <MagicButton
              icon={
                <img
                  src={`${AssetUrls.base}/assets/icons/SendCards/icon-copy.svg`}
                  alt="Copy"
                  style={{ width: 16, height: 16, filter: 'brightness(0) invert(1)' }}
                />
              }
              label={copied ? 'Address Copied!' : 'Copy Address'}
              color="dark-grey"
              onPress={copyAddressToClipboard}
            />
            <a
              href="https://trademagic.gg/articles/shipping"
              target="_blank"
              rel="noopener noreferrer"
              className="packing-slip-guide-link"
            >
              Shipping Guide
            </a>
            <a
              href="https://trademagic.gg/articles/condition-guide"
              target="_blank"
              rel="noopener noreferrer"
              className="packing-slip-guide-link"
            >
              Condition Guide
            </a>
          </div>

          <div className="slip-section">
            <h3>Package Contents:</h3>
            <div className="contents-table">
              <div className="contents-header">
                <span className="col-qty">Qty</span>
                <span className="col-name">Card Name</span>
                <span className="col-set">Set</span>
                <span className="col-foil">Foil</span>
                <span className="col-condition">Cond.</span>
                <span className="col-language">Lang</span>
              </div>
              {trade.items.map((item, index) => (
                <div key={index} className="contents-row">
                  <span className="col-qty">{item.qty}</span>
                  <span className="col-name">{item.name}</span>
                  <span className="col-set" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <SetIcon setIconUrl={item.setIconUrl} setName={item.set} size="sm" />
                    {(item.setCode || item.set).toUpperCase()}
                  </span>
                  <span className="col-foil">{item.foil ? 'Foil' : '—'}</span>
                  <span className="col-condition">{formatCondition(item.condition)}</span>
                  <span className="col-language">{formatLanguage(item.language)}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="slip-section slip-footer-section">
            <div className="slip-footer">
              <div className="trade-info-small">
                <span>Total Items: {trade.items.reduce((sum, item) => sum + item.qty, 0)}</span>
                <span>Prepared: {formatDate()}</span>
                <span>Shipped from: {formatCountry(senderCountry)}</span>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}